import React, { useState } from 'react';
import { useStory } from './StoryContext';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, XIcon, SaveIcon, CheckIcon, ShareIcon } from 'lucide-react';
import { ShareStory } from './ShareStory';
const splitIntoPages = (text: string): string[] => {
  const words = text.split(/\s+/);
  const pages: string[] = [];
  let currentPage: string[] = [];
  let currentWordCount = 0;
  words.forEach(word => {
    if (currentWordCount + 1 > 100) {
      // Find last period to make clean sentence breaks
      const lastPeriodIndex = currentPage.join(' ').lastIndexOf('.');
      if (lastPeriodIndex !== -1 && currentPage.length > 5) {
        const splitIndex = currentPage.join(' ').slice(0, lastPeriodIndex + 1).split(' ').length;
        const remainingWords = currentPage.slice(splitIndex);
        pages.push(currentPage.slice(0, splitIndex).join(' '));
        currentPage = [...remainingWords, word];
        currentWordCount = remainingWords.length + 1;
      } else {
        pages.push(currentPage.join(' '));
        currentPage = [word];
        currentWordCount = 1;
      }
    } else {
      currentPage.push(word);
      currentWordCount++;
    }
  });
  if (currentPage.length > 0) {
    pages.push(currentPage.join(' '));
  }
  return pages;
};
export const EBook = () => {
  const {
    generateStory,
    setShowEbook,
    saveStory,
    previewStory,
    isLoggedIn,
    setTempStory
  } = useStory();
  const [currentPage, setCurrentPage] = useState(0);
  const [isSaved, setIsSaved] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginPromptType, setLoginPromptType] = useState<'save' | 'share'>('save');
  const storyText = previewStory ? previewStory.content : generateStory();
  const isPreview = !!previewStory;
  // Split story into pages with max 100 words each
  const storyPages = splitIntoPages(storyText);
  const pages = [{
    type: 'title',
    content: previewStory ? previewStory.title : 'My Amazing Story',
    subtitle: isPreview ? 'Preview Story' : 'Created with Funny Bunny'
  }, ...storyPages.map(page => ({
    type: 'story',
    content: page.trim()
  })), {
    type: 'end',
    content: 'The End',
    subtitle: isPreview ? 'Add this story to your library!' : 'Thank you for reading!'
  }];
  const nextPage = () => {
    if (currentPage < pages.length - 1) {
      setCurrentPage(curr => curr + 1);
    }
  };
  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(curr => curr - 1);
    }
  };
  const handleSave = () => {
    if (!isLoggedIn) {
      setTempStory({
        title: previewStory ? previewStory.title : 'My Amazing Story',
        content: storyText
      });
      setShowLoginPrompt(true);
      return;
    }
    if (isPreview && previewStory) {
      saveStory(previewStory.title, previewStory.content);
    } else {
      saveStory('My Amazing Story', storyText);
    }
    setIsSaved(true);
    setTimeout(() => {
      setIsSaved(false);
      setShowEbook(false);
    }, 2000);
  };
  const handleShare = () => {
    if (!isLoggedIn) {
      setLoginPromptType('share');
      setShowLoginPrompt(true);
      return;
    }
    setShowShareDialog(true);
  };
  return <div className="fixed inset-0 bg-purple-50 flex flex-col items-center justify-center p-4" data-id="element-384">
      {showLoginPrompt ? <div className="bg-white rounded-2xl w-full max-w-md p-6 text-center" data-id="element-385">
          <h3 className="text-xl font-bold text-purple-700 mb-4" data-id="element-386">
            {loginPromptType === 'save' ? 'Want to save your story?' : 'Want to share your story?'}
          </h3>
          <p className="text-purple-600 mb-6" data-id="element-387">
            Create a parent account to{' '}
            {loginPromptType === 'save' ? 'save and manage' : 'share'} all your
            child's magical stories!
          </p>
          <div className="flex justify-center space-x-4" data-id="element-388">
            <button onClick={() => setShowLoginPrompt(false)} className="px-6 py-2 border-2 border-purple-200 text-purple-600 rounded-full hover:bg-purple-50" data-id="element-389">
              Maybe Later
            </button>
            <button onClick={() => {
          setShowEbook(false);
          window.location.href = '/parent-login'; // You'll need to set up this route
        }} className="px-6 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700" data-id="element-390">
              Create Account
            </button>
          </div>
        </div> : <div className="fixed inset-0 bg-purple-50 flex flex-col items-center justify-center p-4" data-id="element-391">
          <button onClick={() => setShowEbook(false)} className="absolute top-4 right-4 p-2 hover:bg-purple-100 rounded-full" data-id="element-392">
            <XIcon size={24} className="text-purple-700" data-id="element-393" />
          </button>
          {showShareDialog && <ShareStory storyTitle={previewStory ? previewStory.title : 'My Amazing Story'} onClose={() => setShowShareDialog(false)} onShare={handleShare} data-id="element-394" />}
          <div className="w-full max-w-4xl aspect-[3/2] bg-white rounded-lg shadow-xl flex flex-col p-8 relative" data-id="element-395">
            {/* Content Area */}
            <div className="flex-grow flex items-center justify-center" data-id="element-396">
              {pages[currentPage].type === 'title' ? <div className="text-center" data-id="element-397">
                  <h1 className="text-4xl font-bold text-purple-700 mb-4" data-id="element-398">
                    {pages[currentPage].content}
                  </h1>
                  <p className="text-xl text-purple-500" data-id="element-399">
                    {pages[currentPage].subtitle}
                  </p>
                </div> : pages[currentPage].type === 'end' ? <div className="text-center" data-id="element-400">
                  <h2 className="text-4xl font-bold text-purple-700 mb-4" data-id="element-401">
                    {pages[currentPage].content}
                  </h2>
                  <p className="text-xl text-purple-500" data-id="element-402">
                    {pages[currentPage].subtitle}
                  </p>
                </div> : <p className="max-w-2xl text-center text-2xl leading-relaxed" data-id="element-403">
                  {pages[currentPage].content}
                </p>}
            </div>
            {/* Navigation Area */}
            <div className="flex justify-between items-center mt-8 relative" data-id="element-404">
              <button onClick={prevPage} disabled={currentPage === 0} className={`flex items-center p-3 rounded-full transition-colors ${currentPage === 0 ? 'text-gray-300' : 'text-purple-600 hover:bg-purple-100'}`} data-id="element-405">
                <ChevronLeftIcon size={24} className="mr-1" data-id="element-406" />
                <span data-id="element-407">Previous</span>
              </button>
              <span className="text-purple-600 absolute left-1/2 transform -translate-x-1/2" data-id="element-408">
                Page {currentPage + 1} of {pages.length}
              </span>
              <div className="flex items-center" data-id="element-409">
                {currentPage === pages.length - 1 && <>
                    <button onClick={handleShare} className="flex items-center px-4 py-2 rounded-full mr-4 bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors" data-id="element-410">
                      <ShareIcon size={20} className="mr-2" data-id="element-411" />
                      Share Story
                    </button>
                    <button onClick={handleSave} disabled={isSaved} className={`flex items-center px-4 py-2 rounded-full mr-4 transition-colors ${isSaved ? 'bg-green-500 text-white' : 'bg-purple-600 text-white hover:bg-purple-700'}`} data-id="element-412">
                      {isSaved ? <>
                          <CheckIcon size={20} className="mr-2" data-id="element-413" />
                          {isPreview ? 'Added to Library!' : 'Saved to Library!'}
                        </> : <>
                          <SaveIcon size={20} className="mr-2" data-id="element-414" />
                          {isPreview ? 'Add to Library' : 'Save to Library'}
                        </>}
                    </button>
                  </>}
                <button onClick={nextPage} disabled={currentPage === pages.length - 1} className={`flex items-center p-3 rounded-full transition-colors ${currentPage === pages.length - 1 ? 'text-gray-300' : 'text-purple-600 hover:bg-purple-100'}`} data-id="element-415">
                  <span data-id="element-416">Next</span>
                  <ChevronRightIcon size={24} className="ml-1" data-id="element-417" />
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};